import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { buyer: "Buyer", seller: "Seller", admin: "VISMARKS Support" };
const ROLE_COLOR = { buyer: "text-[#007AFF]", seller: "text-amber-400", admin: "text-emerald-400" };

export const DisputeChat = ({ orderId }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/trust/orders/${orderId}/messages`);
      setMessages(data.messages || []);
    } catch {}
  };

  useEffect(() => {
    if (!open) return;
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/trust/orders/${orderId}/messages`, { body });
      setText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid={`dispute-chat-open-${orderId}`} onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full border border-[#007AFF]/40 text-[#007AFF] hover:bg-[#007AFF]/10">
        <MessageSquare className="h-3.5 w-3.5" /> Open dispute chat
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-[#27272A] bg-black/40 p-4" data-testid={`dispute-chat-${orderId}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-widest text-neutral-400 flex items-center gap-1.5">
          <MessageSquare className="h-3.5 w-3.5 text-[#007AFF]" /> Dispute chat
        </div>
        <button onClick={() => setOpen(false)} className="text-xs text-neutral-500 hover:text-white underline"
          data-testid={`dispute-chat-close-${orderId}`}>close</button>
      </div>
      <div className="mt-3 max-h-64 overflow-y-auto space-y-2 pr-1" data-testid={`dispute-messages-${orderId}`}>
        {messages.length === 0 && <div className="text-xs text-neutral-500">No messages yet.</div>}
        {messages.map((m) => (
          <div key={m.id} className={`rounded-lg px-3 py-2 text-xs max-w-[85%] ${m.mine ? "ml-auto bg-[#007AFF]/15 border border-[#007AFF]/25" : "bg-[#1a1a1c] border border-[#27272A]"}`}>
            <div className={`font-bold text-[10px] uppercase tracking-wider ${ROLE_COLOR[m.sender_role] || "text-neutral-400"}`}>
              {m.mine ? "You" : ROLE_LABEL[m.sender_role] || m.sender_role}
            </div>
            <div className="mt-0.5 text-neutral-200 whitespace-pre-wrap break-words">{m.body}</div>
            <div className="mt-1 text-[9px] text-neutral-500">{new Date(m.created_at).toLocaleString()}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="Write a message…" data-testid={`dispute-chat-input-${orderId}`}
          className="flex-1 bg-black/40 border border-[#27272A] rounded-md text-xs px-3 py-2" />
        <button disabled={busy || !text.trim()} onClick={send} data-testid={`dispute-chat-send-${orderId}`}
          className="inline-flex items-center gap-1 text-xs font-bold px-4 py-2 rounded-full bg-[#007AFF] text-white hover:bg-[#0068d9] disabled:opacity-50">
          <Send className="h-3 w-3" /> Send
        </button>
      </div>
    </div>
  );
};
